import { authedGet } from '../../api/http';
import { buildQuery } from '../../api/query';
import type { Benchmarks, NetworkUsage } from '../../data/analytics';
import { readAccountUsage } from './accountReader';
import type { AccountUsage, ReportPeriod, UsageReport } from './model';
import { readUsageReport } from './reportReader';
import { isPrerenderRequest, prerenderInput } from '../prerender/buildInput';
import { isListedSku } from '../../lib/catalogPlatform';

/** No measurements, which every consumer already renders as a dash. Distinct
 *  from zero traffic only in that nothing here was ever observed. */
const EMPTY_BENCHMARKS: Benchmarks = {
  provider: 'AnyAPI',
  window: '30d',
  lastComputedAt: '',
  definitionVersion: 2,
  definitionCutoverAt: '',
  effectiveStartAt: '',
  bySku: [],
};

const EMPTY_NETWORK_USAGE: NetworkUsage = {
  totalRequests: 0,
  totalSpendUsd: 0,
  activeApis: 0,
  categories: 0,
  latencyP50Ms: 0,
  weekly: [],
  byApi: [],
};

/**
 * Measured traffic and reliability, which the platform pages render server-side.
 *
 * On the prerender crawl these come from build input and never from the wire.
 * Fetching per page would be one call per platform per payload from a single
 * address during the crawl, which is the fan-out the build input exists to
 * avoid; a rate-limited subset would then bake dashes into an unpredictable
 * handful of shipped pages. Absent build input means the whole crawl renders a
 * dash - the state the UI already has for an empty window - rather than each
 * page deciding for itself.
 */
const readNetworkUsage = async (): Promise<NetworkUsage> => {
  if (isPrerenderRequest()) {
    return (prerenderInput('networkUsage') as NetworkUsage | undefined) ?? EMPTY_NETWORK_USAGE;
  }
  return authedGet<NetworkUsage>('/usage/network');
};

/** Network traffic with each unlisted SKU's row dropped. The network totals
 *  still count its calls: they are AnyAPI's whole traffic and name nothing. */
export const fetchNetworkUsage = async (): Promise<NetworkUsage> => {
  const usage = await readNetworkUsage();
  return { ...usage, byApi: usage.byApi.filter((api) => isListedSku(api.sku)) };
};

const readBenchmarks = async (): Promise<Benchmarks> => {
  if (isPrerenderRequest()) {
    return (prerenderInput('benchmarks') as Benchmarks | undefined) ?? EMPTY_BENCHMARKS;
  }
  return authedGet<Benchmarks>('/benchmarks');
};

export const fetchBenchmarks = async (): Promise<Benchmarks> => {
  const benchmarks = await readBenchmarks();
  return { ...benchmarks, bySku: benchmarks.bySku.filter((row) => isListedSku(row.sku)) };
};

export const fetchAccountUsage = async (days?: number, timeZone?: string): Promise<AccountUsage> =>
  readAccountUsage(await authedGet<unknown>(`/v1/usage${buildQuery({ days, tz: timeZone })}`));

/** The signed-in account's charged usage over `period`, bucketed into calendar
 *  days in `timeZone` (an IANA zone; the gateway falls back to UTC). */
export const fetchUsageReport = async (period: ReportPeriod, timeZone?: string): Promise<UsageReport> =>
  readUsageReport(await authedGet<unknown>(`/v1/usage/report${buildQuery({ period, tz: timeZone })}`));
